import { WarningIcon } from "lola-framework-ui-test";
import { type JSX, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEvents, safeCapture } from "../../../../analytics";
import type { AmountAnchor } from "../../../../interfaces/amount";
import { useStore } from "../../../../store/globalState";
import { formatMoney } from "../../../../utils";

export interface QuoteInfoProps {
  label: string;
  amount?: string;
  icon?: JSX.Element;
  currency: string;
  sourceCurrency?: string;
  mode?: "info" | "input";
  side?: AmountAnchor;
  derivedAmount?: string;
  exchangeRate?: number;
}

const MAX_FONT = 1.75;
const MIN_FONT = 1.75;

function rawAmountFromDisplay(value: string | undefined): string {
  if (!value) return "";
  const cleaned = value.replace(/[^\d.]/g, "");
  if (!cleaned) return "";
  const dotIndex = cleaned.indexOf(".");
  const intPart =
    dotIndex === -1 ? cleaned : cleaned.substring(0, dotIndex);
  const decPart =
    dotIndex === -1
      ? ""
      : cleaned.substring(dotIndex + 1).replace(/\./g, "").slice(0, 2);
  if (!intPart && !decPart && dotIndex === -1) return "";
  if (dotIndex === -1) return intPart;
  return `${intPart || "0"}.${decPart}`;
}

function rawForEditing(value: string | undefined): string {
  const raw = rawAmountFromDisplay(value);
  if (!raw) return "";
  if (/^\d+\.00$/.test(raw)) return raw.slice(0, -3);
  return raw;
}

export const QuoteInfo: React.FC<QuoteInfoProps> = ({
  label,
  amount,
  currency,
  sourceCurrency,
  mode = "info",
  side = "send",
  derivedAmount,
  exchangeRate,
}) => {
  const { amountState, setAmountState } = useStore();
  const isPinned = mode === "input" && amountState.anchor === side;
  const [amountInput, setAmountInput] = useState<string>(() =>
    isPinned ? rawForEditing(amountState.amount) : ""
  );
  const [focused, setFocused] = useState(false);
  const focusedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [fontSize, setFontSize] = useState(MAX_FONT);
  const { t } = useTranslation();
  const inputId = `amount-input-${side}`;

  useEffect(() => {
    if (!isPinned || focusedRef.current) return;
    setAmountInput(rawAmountFromDisplay(amountState.amount));
  }, [isPinned, amountState.amount]);

  useEffect(() => {
    if (mode !== "input" || isPinned || focusedRef.current) return;
    setAmountInput(rawAmountFromDisplay(derivedAmount));
  }, [mode, isPinned, derivedAmount]);

  useEffect(() => {
    if (!inputRef.current || !containerRef.current) return;
    const input = inputRef.current;
    const container = containerRef.current;
    const containerWidth = container.offsetWidth - 150;
    const span = document.createElement("span");
    span.style.fontSize = `${MAX_FONT}rem`;
    span.style.fontFamily = getComputedStyle(input).fontFamily;
    span.style.whiteSpace = "nowrap";
    span.innerText = input.value || "0";
    document.body.appendChild(span);
    const textWidth = span.offsetWidth;
    document.body.removeChild(span);
    const scale = containerWidth / textWidth;
    let newFont = MAX_FONT * scale;
    newFont = Math.min(MAX_FONT, Math.max(MIN_FONT, newFont));
    setFontSize(newFont);
  }, [amountInput]);

  const normalizeRawAmount = (value: string) => {
    const cleaned = value.replace(/[^\d.]/g, "");
    const dotIndex = cleaned.indexOf(".");
    const intPart = dotIndex === -1 ? cleaned : cleaned.substring(0, dotIndex);
    const decPart =
      dotIndex === -1
        ? ""
        : cleaned
            .substring(dotIndex + 1)
            .replace(/\./g, "")
            .slice(0, 2);
    const hasDot = dotIndex !== -1;
    return { intPart, decPart, hasDot };
  };

  const toRawString = ({
    intPart,
    decPart,
    hasDot,
  }: ReturnType<typeof normalizeRawAmount>) => {
    if (!intPart && !decPart && !hasDot) return "";
    const safeInt = intPart || (hasDot ? "0" : "");
    return hasDot ? `${safeInt}.${decPart}` : safeInt;
  };

  const formatWithGrouping = (raw: string) => {
    if (!raw) return "";
    const [intPart = "", decPart = ""] = raw.split(".");
    const groupedInt =
      intPart.length > 0 ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0";
    return raw.includes(".") ? `${groupedInt}.${decPart}` : groupedInt;
  };

  const getNormalizedFromDisplay = (value: string, caretPos: number) => {
    let raw = "";
    let rawCaret = 0;
    let hasDot = false;
    for (let i = 0; i < value.length; i += 1) {
      const ch = value[i];
      if (/\d/.test(ch)) {
        raw += ch;
        if (i < caretPos) rawCaret += 1;
      } else if (ch === "." && !hasDot) {
        hasDot = true;
        raw += ".";
        if (i < caretPos) rawCaret += 1;
      }
    }
    const normalized = normalizeRawAmount(raw);
    const normalizedRaw = toRawString(normalized);
    return {
      normalizedRaw,
      clampedCaret: Math.min(rawCaret, normalizedRaw.length),
    };
  };

  const getDisplayCaretFromRaw = (displayValue: string, rawCaret: number) => {
    if (rawCaret <= 0) return 0;
    let count = 0;
    for (let i = 0; i < displayValue.length; i += 1) {
      const ch = displayValue[i];
      if (/\d/.test(ch) || ch === ".") {
        count += 1;
        if (count === rawCaret) return i + 1;
      }
    }
    return displayValue.length;
  };

  const onChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const { value } = event.currentTarget;
    const caretPos = input.selectionStart ?? 0;
    const { normalizedRaw, clampedCaret } = getNormalizedFromDisplay(
      value,
      caretPos
    );
    const normalizedParts = normalizeRawAmount(normalizedRaw);
    const limitedIntPart =
      normalizedParts.intPart.length > 5
        ? normalizedParts.intPart.slice(0, 5)
        : normalizedParts.intPart;
    const nextRaw = toRawString({
      ...normalizedParts,
      intPart: limitedIntPart,
    });
    const nextCaret = Math.min(clampedCaret, nextRaw.length);
    const formattedDisplay = formatWithGrouping(nextRaw);

    setAmountInput(nextRaw);
    setAmountState({ amount: nextRaw, anchor: side });

    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const newCaret = getDisplayCaretFromRaw(formattedDisplay, nextCaret);
      inputRef.current.setSelectionRange(newCaret, newCaret);
    });
  };

  const inputWidth = useMemo(() => {
    const displayValue = formatWithGrouping(amountInput);
    if (!displayValue) return "4rem";
    return `${Math.max(displayValue.length, 1)}rem`;
  }, [amountInput]);

  const handleFocus = () => {
    focusedRef.current = true;
    setFocused(true);
    if (mode !== "input") return;
    if (!isPinned) {
      const seed =
        rawForEditing(derivedAmount) || rawForEditing(amountInput);
      setAmountInput(seed);
      // Never pin an empty seed — that wipes the opposite leg and kills pre-quote/quote.
      if (seed) {
        setAmountState({ amount: seed, anchor: side });
      }
      return;
    }
    setAmountInput(rawForEditing(amountInput));
  };

  const handleBlur = () => {
    focusedRef.current = false;
    setFocused(false);
    if (mode !== "input") return;
    const padded = formatMoney(amountInput) || "";
    if (amountState.anchor === side) {
      setAmountInput(padded);
      setAmountState({ amount: padded, anchor: side });
    }
    const numeric = parseFloat(padded || amountInput);
    if (!padded || Number.isNaN(numeric) || numeric <= 0) return;
    safeCapture(getEvents().AMOUNT_SUBMITTED, {
      amount: numeric,
      anchor: side,
      destination_currency: currency,
    });
  };

  const inputValue = formatWithGrouping(amountInput);
  const splitAmount = amount?.trim() ? amount.split(".") : ["0", "00"];
  const showExchangeFooter = side === "receive" && exchangeRate != null;

  return (
    <section
      className={`quote-send ${mode} glass gradient-card`}
      ref={containerRef}
    >
      <section className="quote-send-content">
        <div
          className="quote-send-info"
          onClick={() => inputRef.current?.focus()}
        >
          <p className="quote-label foreground">{label}</p>
          {mode === "info" && (
            <p
              className={`highlight label-amount ${
                amount?.trim() ? "foreground" : "muted-foreground"
              }`}
            >
              ${splitAmount[0]}.<label>{splitAmount[1]}</label>
            </p>
          )}
          {mode === "input" && (
            <>
              <label
                className={`amount-label ${
                  focused || amountInput.length
                    ? "foreground"
                    : "muted-foreground"
                }`}
                htmlFor={inputId}
                style={{
                  fontSize: `${fontSize}rem`,
                  marginRight: 4,
                }}
              >
                $
              </label>
              {/*
                Stays type="text" (not "number") because the display carries
                grouping commas and this input owns caret placement;
                inputMode is what asks mobile for the decimal keypad.
              */}
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                enterKeyHint="done"
                autoComplete="off"
                style={{
                  width: inputWidth,
                  fontSize: `${fontSize}rem`,
                }}
                id={inputId}
                placeholder="0.00"
                className={`amount-input ${
                  focused || amountInput.length
                    ? "foreground"
                    : "muted-foreground"
                }`}
                value={inputValue === "NaN" ? amountInput : inputValue}
                onChange={onChangeAmount}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </>
          )}
        </div>

        <p className="muted-foreground">{currency}</p>
      </section>
      {showExchangeFooter && (
        <section className="quote-info">
          <WarningIcon size={20} className="icon-info" />
          <section>
            <p className="footer-text muted-foreground">
              {t("exchange.rate", {
                from: sourceCurrency ?? currency,
                rate: exchangeRate,
                to: currency,
              })}
            </p>
            <p className="footer-text muted-foreground">
              Estimated exchange rate
            </p>
          </section>
        </section>
      )}
    </section>
  );
};
